import { GetExperimentRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import { ListModelVersionsForModelRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_service_pb';
import { Tag } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/tag_pb';
import { ExperimentService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/experiment_service_connect';
import { RegisteredModelVersionService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_version_service_connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { IStyle, MessageBarType } from '@fluentui/react';
import { ClassNamesFromIStyles, IH2OTheme, useClassNames, useTheme, useToast } from '@h2oai/ui-kit';
import React from 'react';

import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { ENDPOINTS } from './constants';
import { useModelTags } from './ModelTagProvider';

// Adjust this number based on how many tags you want to display.
const MAX_VISIBLE_TAGS = 2;
interface ITaggingWidgetStyles {
  tag: IStyle;
  moreTagsPlaceholder: IStyle;
}

const taggingWidgetStyles = (theme: IH2OTheme): Partial<ITaggingWidgetStyles> => ({
  tag: {
    padding: '2px 4px',
    background: theme.palette?.gray300,
    borderRadius: 24,
    marginRight: 2,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    height: 20,
  },
  moreTagsPlaceholder: {
    padding: '2px 4px',
    background: theme.palette?.gray300,
    borderRadius: 24,
    marginRight: 2,
    lineHeight: '16px',
    height: 20,
  },
});

const ModelTaggingWidget = ({ registeredModelId, modelName }: { registeredModelId: string; modelName?: string }) => {
  const theme = useTheme(),
    classNames = useClassNames<ITaggingWidgetStyles, ClassNamesFromIStyles<ITaggingWidgetStyles>>(
      'modelTaggingWidget',
      taggingWidgetStyles(theme)
    ),
    [tags, setTags] = React.useState<Tag[]>(),
    { addToast } = useToast(),
    { areTagsUpdated, setAreTagsUpdated } = useModelTags(),
    loadStateRef = React.useRef({
      loadingTags: false,
      loadingModelVersion: false,
      settingVersionTags: false,
    }),
    // TODO: Handle tag loading.
    [, setLoading] = React.useState(true),
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
    }),
    evaluateLoading = () => {
      if (
        !loadStateRef.current.loadingTags &&
        !loadStateRef.current.loadingModelVersion &&
        !loadStateRef.current.settingVersionTags
      ) {
        setLoading(false);
      }
    },
    experimentClient = createClient(ExperimentService, storageTransport),
    registerModelVersionClient = createClient(RegisteredModelVersionService, storageTransport),
    getExperiment = React.useCallback(
      async (experimentId: string) => {
        try {
          const response = await experimentClient.getExperiment(
            new GetExperimentRequest({
              id: experimentId,
            })
          );
          const experiment = response?.experiment;
          if (!experiment) console.error('No experiment found in the response.');
          return experiment;
        } catch (err) {
          const message = `Failed to fetch experiment metadata: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          return undefined;
        }
      },
      [addToast]
    ),
    listVersionsTags = React.useCallback(
      async (registeredModelId: string, modelName?: string) => {
        if (!registeredModelId) return;
        loadStateRef.current.loadingModelVersion = true;
        try {
          const listModelVersionsForModelBody = new ListModelVersionsForModelRequest({
            registeredModelId,
          });
          const response = await registerModelVersionClient.listModelVersionsForModel(listModelVersionsForModelBody);
          const modelVersions = response?.modelVersions;
          if (!modelVersions) console.error('No model versions found in the response.');

          const versionExperiments = await Promise.all(
            modelVersions.map((modelVersion) => getExperiment(modelVersion.experimentId))
          );
          const versionTags: Tag[] = [];

          versionExperiments.forEach((versionExperiment) => {
            const tag = versionExperiment?.tag;
            if (tag) {
              tag.forEach((t) => {
                if (!versionTags.some((existingTag) => existingTag.id === t.id)) {
                  versionTags.push(t);
                }
              });
            }
          });

          setTags(versionTags);
        } catch (err) {
          const message = `Failed to fetch tags for model versions of model ${modelName} (${registeredModelId}): ${formatError(
            err
          )}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          setTags(undefined);
        } finally {
          loadStateRef.current.loadingModelVersion = false;
          evaluateLoading();
        }
      },
      [addToast, registerModelVersionClient]
    );

  React.useEffect(() => {
    listVersionsTags(registeredModelId, modelName);
    if (!areTagsUpdated) setAreTagsUpdated(true);
  }, [registeredModelId, modelName, areTagsUpdated]);

  return (
    <div>
      <div style={{ minHeight: 24, display: 'flex', flexWrap: 'nowrap', overflow: 'hidden' }}>
        {tags?.map((tag, index, array) => {
          if (index < MAX_VISIBLE_TAGS) {
            return (
              <span title={tag.displayName} className={classNames.tag} key={tag.id}>
                {tag.displayName}
              </span>
            );
          } else if (index === MAX_VISIBLE_TAGS) {
            return (
              <span
                title={`+${array.length - MAX_VISIBLE_TAGS} more  ${
                  array.length - MAX_VISIBLE_TAGS === 1 ? 'tag' : 'tags'
                }`}
                className={classNames.moreTagsPlaceholder}
                key="more-tags"
              >
                +{array.length - MAX_VISIBLE_TAGS}
              </span>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};
export default ModelTaggingWidget;
